// ArtWorkBackgroundPreview.jsx
import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal, Button, Slider, Row, Col, Select, Spin } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

const { Option } = Select;

const SAMPLE_BACKGROUNDS = [
  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?q=80&w=3087&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1750639258774-9a714379a093?q=80&w=2940&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?q=80&w=1980&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1618220179428-22790b461013?q=80&w=927&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1505691723518-36a5ac3be353?q=80&w=2070&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=1558&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=2069&auto=format&fit=crop",
  "https://plus.unsplash.com/premium_photo-1661778812498-d56d7f09d1fd?q=80&w=1974&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1616047006789-b7af5afb8c20?q=80&w=1480&auto=format&fit=crop",
  "https://plus.unsplash.com/premium_photo-1676320514018-ec119b57dbce?q=80&w=2070&auto=format&fit=crop",
];

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

const ArtWorkBackgroundPreview = ({
  visible,
  onCancel,
  artworkUrl,
  artworkWidth = null,
  artworkHeight = null,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [selectedBgIndex, setSelectedBgIndex] = useState(0);
  const [bgImg, setBgImg] = useState(null);
  const [artImg, setArtImg] = useState(null);

  const [centerPoint, setCenterPoint] = useState(null);
  const [scale, setScale] = useState(1);
  const [opacity, setOpacity] = useState(0.98);

  const [loading, setLoading] = useState(true);

  const draggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // Preload both images in parallel
  const preloadImages = useCallback(async () => {
    setLoading(true);
    try {
      const [bg, art] = await Promise.all([
        loadImage(SAMPLE_BACKGROUNDS[selectedBgIndex] + `?v=${Date.now()}`),
        loadImage(artworkUrl + `?v=${Date.now()}`),
      ]);
      setBgImg(bg);
      setArtImg(art);
    } catch (err) {
      console.error("Image load failed", err);
    } finally {
      setLoading(false);
    }
  }, [artworkUrl, selectedBgIndex]);

  useEffect(() => {
    if (artworkUrl && visible) preloadImages();
  }, [artworkUrl, selectedBgIndex, visible, preloadImages]);

  // Canvas resize
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  useEffect(() => {
    if (!visible) return;
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [visible, resizeCanvas]);

  // Draw scene
  const drawScene = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    ctx.clearRect(0, 0, w, h);

    // background
    if (bgImg) {
      const bgAspect = bgImg.width / bgImg.height;
      const canvasAspect = w / h;
      let drawW, drawH, drawX, drawY;
      if (bgAspect > canvasAspect) {
        drawW = w;
        drawH = w / bgAspect;
        drawX = 0;
        drawY = (h - drawH) / 2;
      } else {
        drawH = h;
        drawW = h * bgAspect;
        drawX = (w - drawW) / 2;
        drawY = 0;
      }
      ctx.drawImage(bgImg, drawX, drawY, drawW, drawH);
    } else {
      ctx.fillStyle = "#222";
      ctx.fillRect(0, 0, w, h);
    }

    // artwork
    if (artImg && centerPoint) {
      let aspectRatio =
        artworkWidth && artworkHeight
          ? artworkWidth / artworkHeight
          : artImg.naturalWidth / artImg.naturalHeight;
      const baseSize = Math.min(w, h) * 0.28;
      const artW = aspectRatio >= 1 ? baseSize * scale : baseSize * scale * aspectRatio;
      const artH = aspectRatio >= 1 ? artW / aspectRatio : baseSize * scale;

      const x = centerPoint.x - artW / 2;
      const y = centerPoint.y - artH / 2;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.shadowColor = "rgba(0,0,0,0.35)";
      ctx.shadowBlur = Math.max(6, artW * 0.04);
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = Math.max(6, artW * 0.03);
      ctx.drawImage(artImg, x, y, artW, artH);
      ctx.restore();
    }
  }, [bgImg, artImg, centerPoint, scale, opacity, artworkWidth, artworkHeight]);

  useEffect(() => {
    if (!loading && visible) drawScene();
  }, [loading, bgImg, artImg, centerPoint, scale, opacity, visible, drawScene]);

  // Pointer controls
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toCanvas = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const down = (e) => {
      e.preventDefault();
      const { x, y } = toCanvas(e);
      if (centerPoint && artImg) {
        let ar = artworkWidth && artworkHeight ? artworkWidth / artworkHeight : artImg.width / artImg.height;
        const baseSize = Math.min(canvas.clientWidth, canvas.clientHeight) * 0.28;
        const artW = ar >= 1 ? baseSize * scale : baseSize * scale * ar;
        const artH = ar >= 1 ? artW / ar : baseSize * scale;
        const artX = centerPoint.x - artW / 2;
        const artY = centerPoint.y - artH / 2;
        if (x >= artX && x <= artX + artW && y >= artY && y <= artY + artH) {
          draggingRef.current = true;
          dragOffsetRef.current = { x: x - centerPoint.x, y: y - centerPoint.y };
          return;
        }
      }
      setCenterPoint({ x, y });
    };

    const move = (e) => {
      if (!draggingRef.current) return;
      const { x, y } = toCanvas(e);
      setCenterPoint({ x: x - dragOffsetRef.current.x, y: y - dragOffsetRef.current.y });
    };

    const up = () => (draggingRef.current = false);

    const wheel = (e) => {
      if (e.deltaY === 0) return;
      const delta = -e.deltaY / 1000;
      setScale((s) => Math.min(3, Math.max(0.3, +(s + delta).toFixed(2))));
    };

    canvas.addEventListener("pointerdown", down, { passive: false });
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    canvas.addEventListener("wheel", wheel);

    return () => {
      canvas.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      canvas.removeEventListener("wheel", wheel);
    };
  }, [centerPoint, artImg, scale, artworkWidth, artworkHeight]);

  const resetPlacement = () => {
    setCenterPoint(null);
    setScale(1);
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="100%"
      style={{ top: 0, padding: 0 }}
      bodyStyle={{ padding: 0, height: "100vh", background: "#000", overflow: "hidden" }}
    >
      <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#000",
              zIndex: 10,
            }}
          >
            <Spin size="large" tip="Loading artwork & background..." />
          </div>
        )}

        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block", cursor: "move" }}
        />

       <div
  style={{
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: "rgba(0,0,0,0.65)",
    padding: "12px 16px",
    backdropFilter: "blur(10px)",
    zIndex: 5,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  }}
>
  {/* Header */}
  <div
    style={{
      color: "#fff",
      fontWeight: 600,
      fontSize: "16px",
      marginBottom: 10,
      textAlign: "center",
      letterSpacing: "0.5px",
    }}
  >
    🖼️ Artwork Background Preview
  </div>

  {/* Controls */}
  <Row
    gutter={[16, 16]}
    justify="center"
    align="middle"
    style={{
      width: "100%",
      maxWidth: 980,
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-evenly",
      textAlign: "center",
    }}
  >
    <Col xs={24} sm={12} md={8} lg={6}>
      <div style={{ color: "#fff", marginBottom: 6, fontSize: "14px" }}>
        Background
      </div>
      <Select
        value={selectedBgIndex}
        onChange={(v) => setSelectedBgIndex(v)}
        style={{ width: "100%", maxWidth: 240 }}
        options={SAMPLE_BACKGROUNDS.map((url, idx) => ({
          value: idx,
          label: `Background ${idx + 1}`,
        }))}
        dropdownStyle={{ backgroundColor: "#111", color: "#fff" }}
      />
    </Col>

    <Col xs={24} sm={12} md={8} lg={6}>
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: 4,
        }}
      >
        <Button
          icon={<ReloadOutlined />}
          onClick={resetPlacement}
          style={{ minWidth: 100 }}
        >
          Reset
        </Button>
        <Button danger onClick={onCancel} style={{ minWidth: 100 }}>
          Close
        </Button>
      </div>
    </Col>
  </Row>
</div>

      </div>
    </Modal>
  );
};

export default ArtWorkBackgroundPreview;
